import { supabase } from '../lib/supabase';

// Enhanced business form service for handling complete business creation
export const businessFormService = {
  // Check if user already has a business listing
  async checkExistingBusiness(userId) {
    try {
      if (!userId) {
        return { data: null, error: 'User ID is required' };
      }

      const { data: existingBusinesses, error } = await supabase?.from('businesses')?.select('id, name, status')?.eq('owner_id', userId)?.limit(1);

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data: existingBusinesses || [], error: null };
    } catch (error) {
      console.error('Error checking existing business:', error);
      return { data: null, error: 'Failed to check existing businesses' };
    }
  },

  // Upload image to Supabase storage
  async uploadImage(file, filePath) {
    try {
      if (!file) return { data: null, error: 'No file provided' };

      const { data, error } = await supabase?.storage?.from('business-images')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        return { data: null, error: error?.message };
      }

      // Get public URL for the uploaded file
      const { data: { publicUrl } } = supabase?.storage?.from('business-images')?.getPublicUrl(data?.path);

      return { data: { path: data?.path, publicUrl }, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to upload image' };
    }
  },

  // Create business hours entries
  async createBusinessHours(businessId, openingHours) {
    try {
      if (!openingHours || typeof openingHours !== 'object') {
        return { data: [], error: null };
      }

      const daysMapping = {
        monday: 'monday',
        tuesday: 'tuesday', 
        wednesday: 'wednesday',
        thursday: 'thursday',
        friday: 'friday',
        saturday: 'saturday',
        sunday: 'sunday'
      };

      const hoursToInsert = Object.entries(openingHours)?.filter(([day, hours]) => hours && hours?.trim() && daysMapping?.[day])?.map(([day, hours]) => {
          const hoursStr = hours?.trim();
          let openTime = null;
          let closeTime = null;
          let isClosed = hoursStr?.toLowerCase() === 'closed';

          if (!isClosed && hoursStr?.includes('-')) {
            const [open, close] = hoursStr?.split('-')?.map(t => t?.trim());
            openTime = open;
            closeTime = close;
          }
          
          return {
            business_id: businessId,
            day_of_week: daysMapping?.[day],
            open_time: openTime,
            close_time: closeTime,
            is_closed: isClosed
          };
        });

      if (hoursToInsert?.length === 0) return { data: [], error: null };

      const { data, error } = await supabase?.from('business_hours')?.insert(hoursToInsert)?.select();

      return { data: data || [], error: error?.message || null };
    } catch (error) {
      return { data: [], error: 'Failed to create business hours' };
    }
  },

  // Create business specializations/key features
  async createBusinessSpecializations(businessId, keyFeatures) {
    try {
      if (!keyFeatures || !Array.isArray(keyFeatures) || keyFeatures?.length === 0) {
        return { data: [], error: null };
      }

      const specializationsToInsert = keyFeatures?.filter(feature => feature && feature?.trim())?.map((feature, index) => ({
          business_id: businessId,
          name: feature?.trim(),
          type: index === 0 ? 'primary' : 'additional'
        }));

      if (specializationsToInsert?.length === 0) return { data: [], error: null };

      const { data, error } = await supabase?.from('business_specializations')?.insert(specializationsToInsert)?.select();

      return { data: data || [], error: error?.message || null };
    } catch (error) {
      return { data: [], error: 'Failed to create business specializations' };
    }
  },

  // Create business images record
  async createBusinessImage(businessId, imageUrl, isFeatured = false, altText = '', displayOrder = 0) {
    try {
      const { data, error } = await supabase?.from('business_images')?.insert({
          business_id: businessId,
          image_url: imageUrl,
          is_featured: isFeatured,
          alt_text: altText,
          display_order: displayOrder
        })?.select()?.single();

      return { data, error: error?.message || null };
    } catch (error) {
      return { data: null, error: 'Failed to create business image record' };
    }
  },

  // Complete business submission with all related data
  async submitBusinessWithDetails(formData) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) {
        return { data: null, error: 'You must be logged in to create a business.' };
      }

      // Check if user already has a business listing
      const { data: existingBusinesses, error: checkError } = await supabase
        ?.from('businesses')
        ?.select('id')
        ?.eq('owner_id', user?.id)
        ?.limit(1);

      if (checkError) {
        return { data: null, error: 'Failed to check existing businesses.' };
      }

      if (existingBusinesses?.length > 0) {
        return { data: null, error: 'You can only create one business listing per account. Please edit your existing business instead.' };
      }

      let logoUrl = null;
      let coverImageUrl = null;

      // Upload logo if provided
      if (formData?.logo) {
        const logoPath = `${user?.id}/logos/logo_${Date.now()}_${formData?.logo?.name}`;
        const logoResult = await this.uploadImage(formData?.logo, logoPath);
        if (logoResult?.error) {
          return { data: null, error: `Failed to upload logo: ${logoResult?.error}` };
        }
        logoUrl = logoResult?.data?.publicUrl;
      }

      // Upload hero/cover image if provided
      if (formData?.heroImage) {
        const heroPath = `${user?.id}/covers/cover_${Date.now()}_${formData?.heroImage?.name}`;
        const heroResult = await this.uploadImage(formData?.heroImage, heroPath);
        if (heroResult?.error) {
          return { data: null, error: `Failed to upload cover image: ${heroResult?.error}` };
        }
        coverImageUrl = heroResult?.data?.publicUrl;
      }

      // Prepare business data for insertion
      const businessData = {
        name: formData?.businessName?.trim(),
        description: formData?.businessDescription?.trim(),
        short_description: formData?.businessDescription?.length > 200 
          ? formData?.businessDescription?.substring(0, 200) + '...' 
          : formData?.businessDescription?.trim(),
        category_id: formData?.category,
        logo_url: logoUrl,
        cover_image_url: coverImageUrl,
        email: formData?.contact?.email?.trim(),
        phone: formData?.contact?.phone?.trim(),
        website_url: formData?.contact?.website?.trim() || null,
        address_line1: formData?.location?.address?.trim(),
        city: formData?.location?.city?.trim(),
        county: formData?.location?.county?.trim(),
        postcode: formData?.location?.postcode?.trim(),
        country: 'United Kingdom',
        average_rating: formData?.rating ? parseFloat(formData?.rating) : 0.0,
        total_reviews: formData?.reviewCount || 0,
        owner_id: user?.id,
        status: 'active' // Auto-approve all businesses
      };

      // Create business record
      const { data: business, error: businessError } = await supabase?.from('businesses')?.insert(businessData)?.select()?.single();

      if (businessError) {
        return { data: null, error: businessError?.message };
      }

      // Create related data concurrently
      const promises = [];

      // Create business hours
      if (formData?.location?.openingHours) {
        promises?.push(this.createBusinessHours(business?.id, formData?.location?.openingHours));
      }

      // Create business specializations/key features
      if (formData?.keyFeatures?.length > 0) {
        promises?.push(this.createBusinessSpecializations(business?.id, formData?.keyFeatures));
      }

      // Create business image records
      if (logoUrl) {
        promises?.push(this.createBusinessImage(business?.id, logoUrl, false, 'Business Logo', 1));
      }
      
      if (coverImageUrl) {
        promises?.push(this.createBusinessImage(business?.id, coverImageUrl, true, 'Business Cover Image', 0));
      }

      // Execute all promises and handle any errors
      if (promises?.length > 0) {
        const results = await Promise.allSettled(promises);
        const errors = results?.filter(result => result?.status === 'rejected' || result?.value?.error)?.map(result => result?.reason || result?.value?.error);
        
        if (errors?.length > 0) {
          console.warn('Some related data creation failed:', errors);
          // Don't fail the entire operation, just log warnings
        }
      }

      return { data: business, error: null };
    } catch (error) {
      console.error('Error submitting business:', error);
      return { data: null, error: 'Failed to submit business. Please try again.' };
    }
  },

  // Update business with complete details
  async updateBusinessWithDetails(businessId, formData) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) {
        return { data: null, error: 'You must be logged in to update a business.' };
      }

      // Check if user owns this business
      const { data: existingBusiness, error: checkError } = await supabase?.from('businesses')?.select('owner_id')?.eq('id', businessId)?.single();

      if (checkError || existingBusiness?.owner_id !== user?.id) {
        return { data: null, error: 'You can only edit your own businesses.' };
      }

      let logoUrl = null;
      let coverImageUrl = null;

      // Upload new logo if provided
      if (formData?.logo) {
        const logoPath = `${user?.id}/logos/logo_${Date.now()}_${formData?.logo?.name}`;
        const logoResult = await this.uploadImage(formData?.logo, logoPath);
        if (!logoResult?.error) {
          logoUrl = logoResult?.data?.publicUrl;
        }
      }

      // Upload new hero/cover image if provided
      if (formData?.heroImage) {
        const heroPath = `${user?.id}/covers/cover_${Date.now()}_${formData?.heroImage?.name}`;
        const heroResult = await this.uploadImage(formData?.heroImage, heroPath);
        if (!heroResult?.error) {
          coverImageUrl = heroResult?.data?.publicUrl;
        }
      }

      // Prepare update data
      const updateData = {
        name: formData?.businessName?.trim(),
        description: formData?.businessDescription?.trim(),
        short_description: formData?.businessDescription?.length > 200 
          ? formData?.businessDescription?.substring(0, 200) + '...' 
          : formData?.businessDescription?.trim(),
        category_id: formData?.category,
        email: formData?.contact?.email?.trim(),
        phone: formData?.contact?.phone?.trim(),
        website_url: formData?.contact?.website?.trim() || null,
        address_line1: formData?.location?.address?.trim(),
        city: formData?.location?.city?.trim(),
        county: formData?.location?.county?.trim(),
        postcode: formData?.location?.postcode?.trim(),
        updated_at: new Date()?.toISOString()
      };

      // Only update image URLs if new ones were uploaded
      if (logoUrl) updateData.logo_url = logoUrl;
      if (coverImageUrl) updateData.cover_image_url = coverImageUrl;

      // Update business record
      const { data: business, error: businessError } = await supabase?.from('businesses')?.update(updateData)?.eq('id', businessId)?.select()?.single();

      if (businessError) {
        return { data: null, error: businessError?.message };
      }

      return { data: business, error: null };
    } catch (error) {
      console.error('Error updating business:', error);
      return { data: null, error: 'Failed to update business. Please try again.' };
    }
  }
};

export default businessFormService;